import { Botao } from '../../components/Botao'
import { CampoDeSelecao } from '../../components/campos'
import { IconeSeta } from '../../components/Icones'
import { TEXTOS } from '../../lib/glossario'
import type { MesComAtividade } from '../../types/repositorios'
import { competenciaPorExtenso } from '../chamados/competencia'
import { competenciaAtual, mesesDoSeletor, mesVizinho } from './setores'

interface Props {
  competencia: string
  meses: MesComAtividade[]
  aoEscolher: (competencia: string) => void
}

/** O piso da navegação: o mês em que o contrato começou. Antes dele não há o que coletar. */
const PRIMEIRO_MES = '2026-05'

/**
 * Navegação por mês: um passo para trás, um para frente, e a lista dos meses com dado.
 *
 * Os botões existem além da lista porque a pergunta mais comum é "e o mês passado?" — um clique,
 * sem abrir a lista. O passo para frente para no mês corrente: mês futuro não tem atividade e o
 * servidor o recusa.
 *
 * @param props o mês escolhido, os meses com dado e o que fazer ao trocar
 * @returns os controles de mês
 */
export function SeletorDeMes({ competencia, meses, aoEscolher }: Props) {
  const t = TEXTOS.repositorios
  const atual = competenciaAtual()
  const opcoes = mesesDoSeletor(
    meses.map((m) => m.competencia),
    atual,
  )
  const porCompetencia = new Map(meses.map((m) => [m.competencia, m]))

  return (
    <div className="flex flex-wrap items-end gap-2">
      <Botao
        aparencia="secundario"
        tamanho="pequeno"
        onClick={() => aoEscolher(mesVizinho(competencia, -1))}
        disabled={competencia <= PRIMEIRO_MES}
        aria-label={t.mesAnterior}
        title={t.mesAnterior}
      >
        <IconeSeta className="h-4 w-4 rotate-180" />
      </Botao>
      <div className="min-w-64">
        <CampoDeSelecao
          rotulo={t.escolherMes}
          ajuda={t.escolherMesAjuda}
          value={opcoes.includes(competencia) ? competencia : ''}
          onChange={(evento) => aoEscolher(evento.target.value)}
        >
          {!opcoes.includes(competencia) && <option value="">{competenciaPorExtenso(competencia)}</option>}
          {opcoes.map((mes) => (
            <option key={mes} value={mes}>
              {rotuloDaOpcao(mes, porCompetencia.get(mes))}
            </option>
          ))}
        </CampoDeSelecao>
      </div>
      <Botao
        aparencia="secundario"
        tamanho="pequeno"
        onClick={() => aoEscolher(mesVizinho(competencia, 1))}
        disabled={competencia >= atual}
        aria-label={t.mesSeguinte}
        title={t.mesSeguinte}
      >
        <IconeSeta className="h-4 w-4" />
      </Botao>
    </div>
  )
}

/** O mês por extenso e, quando há dado, quantos commits — a mesma convenção do seletor de competência. */
function rotuloDaOpcao(mes: string, dado: MesComAtividade | undefined): string {
  const nome = competenciaPorExtenso(mes)
  if (!dado) {
    return nome
  }
  return `${nome} · ${dado.commits} ${dado.commits === 1 ? 'commit' : 'commits'}`
}
